// Synth / city pop (docs/AUDIO_DESIGN.md §12): 80s Japanese city pop through a cassette. A Juno-style poly synth on a
// stereo chorus comps 16th stabs and sustained pads over royal-road and "Just the Two of Us" changes, a resonant synth
// bass pops octaves and syncopations that land on the root at every change, a tight kick with snare + clap on the
// backbeat under 16th hats, and a square or FM glass lead. Straighter and a little brighter than jazzhop, still lofi.
import { bassNote } from '../harmony.js';
import { createPoly, createSynthBass, createSynthLead, createCityKit } from './synth-voices.js';

const mod12 = n => ((n % 12) + 12) % 12;
const ENERGY = { chill: 0, balanced: 1, upbeat: 2 };

/** Bass cells, one letter a 16th: R root · o octave pop (short) · 5 fifth · . rest; 'kick' follows the kick grid. */
const BASS_CELLS = {
  hold:  'R...............',
  lazy:  'R.......R.....o.',
  pop:   'R..o..R...R.o...',
  sync:  'R..R..5...R..R..',
  funk:  'R..oR.o.R..o.Ro.',
  disco: 'R.o.R.o.R.o.R.o.',
};
//                 per energy: weights of the track's main cell (the second one is drawn from the same table)
const BASS_POOL = [
  { hold: 1.2, lazy: 1.4, kick: 1, pop: 0.5 },
  { lazy: 0.6, kick: 1, pop: 1.2, sync: 1, funk: 0.6, disco: 0.3 },
  { pop: 1.2, sync: 1, funk: 1, disco: 1, kick: 0.4 },
];
const pickW = (r, w) => {
  let u = r() * Object.values(w).reduce((a, b) => a + b, 0);
  for (const [k, v] of Object.entries(w)) if ((u -= v) < 0) return k;
  return Object.keys(w)[0];
};

export default {
  id: 'synth', name: 'Synth', desc: 'Analog synth pads and bass with an 80s city-pop feel',
  //            chill                                   balanced                                  upbeat
  vary: 'colour',
  energy: {
    stretch:  [0.35,                                    0.2,                                      0.1],
    bpm:      [[70, 79],                                [78, 92],                                 [88, 100]],
    swing:    [[50, 53],                                [50, 53],                                 [50, 52]],
    snareLag: [[0.004, 0.01],                           [0.002, 0.008],                           [0, 0.005]],
    groove:   [[{ lazy: 1.4, city: 1, drive: 0.3 }, { lazy: 1, city: 1.2, drive: 0.6, bounce: 0.3 }],
               [{ city: 1, lazy: 0.8, drive: 0.8, bounce: 0.4 }, { city: 1, drive: 1.2, bounce: 1, lazy: 0.4, disco: 0.3 }],
               [{ drive: 1.2, bounce: 1, disco: 0.6, city: 0.6 }, { drive: 1.2, bounce: 1.1, disco: 0.8, city: 0.6 }]],
    kitLp:    [[6000, 8000],                            [7000, 9000],                             [7500, 9500]],
    epLp:     [[3200, 4500],                            [3800, 5500],                             [4500, 6500]],   // the pad bus LP
    strum:    [[0.004, 0.012],                          [0.003, 0.009],                           [0.002, 0.006]],
    epVel:    [[0.5, 0.62],                             [0.55, 0.68],                             [0.6, 0.72]],
    comp:     [{ pad: [0.8, 1.4], swell: [0.3, 0.8], lift: [0.2, 0.6], mix: [0.1, 0.4], stab: [0, 0.15], skank: [0, 0.1] },
               { pad: [0.3, 0.8], swell: [0.2, 0.6], lift: [0.3, 0.8], mix: [0.4, 0.9], stab: [0.3, 0.8], skank: [0.1, 0.4] },
               { pad: [0.05, 0.3], swell: [0.1, 0.3], lift: [0.3, 0.7], mix: [0.5, 1], stab: [0.6, 1.2], skank: [0.4, 0.9] }],
    push:     [0.15,                                    0.28,                                     0.4],
    legato:   [0.75,                                    0.55,                                     0.35],
    lead:     [0.55,                                    0.7,                                      0.8],
    softLead: [0.55,                                    0.55,                                     0.5],            // 'soft' plays the square lead, else glass
    tone:     [[5200, 7500],                            [6500, 9000],                             [7500, 9800]],
    wowCents: [[4, 8],                                  [3, 7],                                   [2.5, 6]],
    dust:     [[0.7, 1.3],                              [0.5, 1.1],                               [0.4, 0.9]],
  },
  progressions: {
    major: {
      C1: [[5, 'maj9', 4], [7, '7', 4], [4, 'm7', 4], [9, 'm9', 4]],                                  // royal road IV V iii vi
      C2: [[5, 'maj7', 4], [4, '7s9', 4], [9, 'm9', 4], [7, 'm7', 2], [0, '9', 2]],                   // Just the Two of Us
      C3: [[0, 'maj9', 4], [9, 'm7', 4], [2, 'm9', 4], [7, '7sus4', 4]],                              // I vi ii V7sus4
      C4: [[5, 'maj9', 4], [4, 'm7', 4], [9, '7', 4], [2, 'm9', 2], [7, '7sus4', 2]],                 // IV iii VI7 ii V
      C5: [[0, 'maj9', 4], [5, 'maj9', 4], [4, 'm7', 2], [9, '7', 2], [2, 'm9', 2], [7, '7sus4', 2]],
      C6: [[0, 'add9', 4], [0, '7', 4], [5, 'maj9', 4], [7, '7sus4', 4]],                             // I I7 (V/IV) IV V7sus4
    },
    minor: {
      c1: [[8, 'maj9', 4], [7, 'm7', 4], [0, 'm9', 4], [0, 'm11', 4]],                                // ♭VI v i (Plastic Love)
      c2: [[8, 'maj7', 4], [7, '7s9', 4], [0, 'm9', 4], [10, 'm7', 2], [3, '9', 2]],                  // Two of Us in minor
      c3: [[0, 'm9', 4], [5, '9', 4], [10, 'maj7', 4], [3, 'maj9', 4]],                               // dorian i IV ♭VII ♭III
      c4: [[5, 'm9', 4], [10, '7sus4', 4], [3, 'maj9', 4], [8, 'maj7', 2], [7, '7s9', 2]],            // iv ♭VII ♭III ♭VI V
      c5: [[0, 'madd9', 4], [8, 'maj9', 4], [10, 'add9', 4], [7, 'm7', 2], [7, '7s9', 2]],
    },
  },
  turnarounds: { major: [[2, 'm7', 2], [7, '7sus4', 2]], minor: [[2, 'm7b5', 2], [7, '7sus4', 2]] },   // iim7 – V7sus4
  grids: {
    lazy:   { K: 'x......x..x.....', S: '....x.......x...', H: 'x.x.x.x.x.x.x.o.' },
    city:   { K: 'x.......x.x.....', S: '....x.......x...', C: '....x.......x...', H: 'x.x.x.x.x.x.x.x.' },
    drive:  { K: 'x.....x.x.......', S: '....x.......x...', C: '....x.......x...', H: 'xsxaxsxaxsxaxsxa' },
    bounce: { K: 'x..x..x...x.....', S: '....x.......x...', C: '............x...', H: 'x.xxx.xxx.xxx.xx' },
    disco:  { K: 'x...x...x...x...', S: '....x.......x...', C: '....x.......x...', H: '..o...o...o...o.', X: 'xsxsxsxsxsxsxsxs' },
    break:  { K: 'x.........x.....', R: '....r.......r...', H: 'x.x.x.x.x.x.x.x.', X: '..x...x...x...x.' },
  },
  fills: {
    T1: { roll: [[12, 0.8], [13, 0.65], [14, 0.75], [15, 0.85]], voice: 'tom' },   // tom run, high → floor
    T2: { roll: [[10, 0.75], [12, 0.8], [14, 0.85]], voice: 'tom' },
    S1: { roll: [[12, 0.9], [13, 0.35], [14, 0.5], [15, 0.7]] },
    O1: { open: 14 },
    X1: { stop: 12 },
  },
  // comping cells [step, length, vel]: sustained pads and syncopated 16th stabs (a cell per bar, weighted by `comp`)
  comps: {
    pad:   [[0, 16, 0.9]],
    swell: [[0, 8, 0.9], [8, 8, 0.8]],
    lift:  [[0, 6, 0.9], [6, 10, 0.8]],
    mix:   [[0, 8, 0.9], [10, 1, 0.85], [11, 2, 0.8], [14, 2, 0.85]],
    stab:  [[0, 2, 1], [3, 1, 0.8], [6, 2, 0.9], [10, 1, 0.85], [13, 2, 0.85]],
    skank: [[2, 1, 0.85], [6, 1, 0.9], [10, 1, 0.85], [14, 1, 0.9]],
  },
  keysLevel: 1.41,                                                   // keys only: +3 dB, as jazzhop
  plan(plan, { r, energy }) {
    const e = ENERGY[energy] ?? 1, pick = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d);
    const main = plan.drums.pattern === 'disco' && r() < 0.6 ? 'disco' : pickW(r, BASS_POOL[e]), rate = pick(0.4, 0.9, 2);
    plan.synth = {
      detune: pick(6, 10, 1), cutoff: pick(...[[700, 1100], [850, 1400], [1000, 1700]][e], 0), env: pick(1.6, 2.6, 2),
      res: pick(0.9, 2.2, 2), atk: pick(...[[0.03, 0.06], [0.02, 0.045], [0.012, 0.03]][e]), rel: pick(0.14, 0.26),
      chorus: { rate, depth: +(pick(6, 10, 1) / (1731 * 4 * rate)).toFixed(5) },   // triangle sweep: ±6-10 ¢ = 1731·4·depth·rate
      bass: { sq: pick(0.3, 0.7, 2), q: pick(2, 4.5, 2), sus: pick(3, 4.5, 2), env: pick(0.8, 1.2, 2), dec: pick(0.07, 0.14),
        cells: [main, pickW(r, BASS_POOL[e])], approach: [0.35, 0.45, 0.5][e] },
      lead: { vib: pick(8, 14, 1), glide: pick(0.025, 0.05) },
    };
    Object.assign(plan.drums.kit, {
      kickF0: pick(165, 185, 1), kickF1: pick(48, 55, 1), kickDecay: pick(0.085, 0.115), snareDecay: pick(0.07, 0.1),
      hatHP: pick(7000, 9000, 0), hatClosed: pick(0.009, 0.014),
    });
    plan.drums.perc = { clap: { f: pick(1100, 1500, 0) }, shaker: { hp: 5000, decay: 0.028 }, gain: { clap: pick(0.8, 1, 2), shaker: 0.8 } };
    if (plan.lead) plan.lead.voice = plan.lead.timbre === 'soft' ? 'square' : 'glass';
  },
  /** One bar of synth bass (main groove only; intro, break and outro keep the default line). */
  bass({ plan, st, b, mode, chords, nextChords, chordAt, kicks, stop }) {
    if (mode !== 'kick') return null;
    const S = plan.synth.bass, r = st.rS;
    const id = b.j % 4 === 3 ? S.cells[r() < 0.6 ? 1 : 0] : S.cells[r() < 0.85 ? 0 : 1];
    let hits = id === 'kick' ? kicks.map(s => [s, 'R']) : [...BASS_CELLS[id]].map((c, s) => [s, c]).filter(([, c]) => c !== '.');
    for (const c of chords) { const s0 = c.start * 4; hits = hits.filter(([s]) => s !== s0); hits.push([s0, 'R']); }   // a root on every change
    hits.sort((x, y) => x[0] - y[0]);
    const last = chords[chords.length - 1], nx = nextChords && nextChords[0];
    if (nx && nx.root !== last.root && r() < S.approach) {                    // approach the next root on step 14 or 15
      const s = r() < 0.5 ? 14 : 15;
      hits = hits.filter(([h]) => h < s); hits.push([s, 'A']);
    }
    if (stop != null) hits = hits.filter(([s]) => s < stop);
    const end = stop ?? 16;
    return hits.map(([s, c], k) => {
      const root = bassNote(mod12(plan.key.tonic + chordAt(s).root), st.prevBass);
      let midi = root, len = (k + 1 < hits.length ? hits[k + 1][0] : end) - s, vel = s === 0 ? 0.92 : s % 4 ? 0.74 : 0.84, slide = false;
      if (c === 'o') { midi = root + 12; len = Math.min(len, 1.2); vel = 0.78; slide = r() < 0.12; }
      else if (c === 'A') {
        const tg = bassNote(mod12(plan.key.tonic + nx.root), root), u = r();
        midi = u < 0.45 ? tg - 1 : u < 0.7 ? tg + 1 : tg - 5; vel = 0.76; len = Math.min(len, 1.5); slide = r() < 0.25;
      } else {
        if (c === '5') midi = root + 7 <= 50 ? root + 7 : root - 5;
        len *= plan.bass.factor;
        st.prevBass = root;
      }
      return { s, midi, vel, dur: Math.max(0.5, len), slide };
    });
  },
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 };
    return {
      ep: createPoly(ac, dry, wet, plan.synth, plan.ep.lp, { ...o, cap: ctx.cap, r: ctx.r, log: ctx.log }),
      bass: createSynthBass(ac, dry, plan.synth.bass, o),
      kit: createCityKit(ac, dry, wet, plan.drums.kit, { ...o, stepDur: ctx.stepDur }),
      lead: plan.lead ? createSynthLead(ac, dry, wet, plan.lead, plan.synth.lead, { ...o, stepDur: ctx.stepDur }) : null,
    };
  },
};
